import { Box, TextField } from '@mui/material'

// 24 distinct preset colors — 2-3 per hue for broad, recognizable coverage
const PRESET_COLORS: string[] = [
  '#E53935', '#C62828',            // red
  '#E91E63', '#AD1457',            // pink
  '#9C27B0', '#6A1B9A',            // purple
  '#5C6BC0', '#283593',            // indigo
  '#1E88E5', '#1565C0',            // blue
  '#039BE5',                        // light blue
  '#00ACC1',                        // cyan
  '#00897B', '#00695C',            // teal
  '#43A047', '#2E7D32',            // green
  '#7CB342',                        // light green
  '#FDD835',                        // yellow
  '#FFB300',                        // amber
  '#FB8C00', '#E65100',            // orange
  '#F4511E',                        // deep orange
  '#6D4C41', '#4E342E',            // brown
  '#546E7A',                        // blue-grey
  '#607D8B',                        // grey
]

interface ColorPickerProps {
  value: string
  onChange: (color: string) => void
}

/** A simple grid of 24 preset color swatches. Tap to select. */
export default function ColorPicker({ value, onChange }: ColorPickerProps) {
  const isValidHex = /^#[0-9a-fA-F]{6}$/.test(value)

  return (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(36px, 1fr))',
          gap: 0.75,
          mb: 1.5,
        }}
      >
        {PRESET_COLORS.map((color) => {
          const isSelected = value.toLowerCase() === color.toLowerCase()
          return (
            <Box
              key={color}
              onClick={() => onChange(color)}
              aria-label={color}
              title={color}
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                bgcolor: color,
                cursor: 'pointer',
                border: isSelected ? 3 : 2,
                borderColor: isSelected ? 'text.primary' : 'divider',
                outline: isSelected ? '2px solid' : 'none',
                outlineColor: 'primary.main',
                outlineOffset: 2,
                transition: 'transform 0.1s, border 0.1s',
                '&:active': { transform: 'scale(0.9)' },
                // Larger tap target on mobile
                '@media (pointer: coarse)': {
                  width: 40,
                  height: 40,
                },
              }}
            />
          )
        })}
      </Box>

      {/* Fallback manual hex input for custom colors */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          size="small"
          label="Or enter hex"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#1976d2"
          error={!isValidHex && value.length > 0}
          helperText={!isValidHex && value.length > 0 ? 'Invalid hex' : undefined}
          sx={{ flex: 1 }}
          slotProps={{ htmlInput: { maxLength: 7 } }}
        />
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            bgcolor: isValidHex ? value : '#ccc',
            border: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        />
      </Box>
    </Box>
  )
}
